import React, { useCallback } from 'react';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faArrowToRight } from '@fortawesome/pro-regular-svg-icons';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ButtonWithFontAwesome from '@monorepo/shared/src/components/common/Buttons/ButtonWithFontAwesome';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import {
  tA11y,
  translate,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import { FONT_GSS_FUTURA_SEMI_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import ResourcePlayer from '@monorepo/shared/src/components/modules/Resources/ResourcePlayer';
import useSupportResourcesStore from 'graph/state/query-hooks/use-support-resources';
import { Resource } from '@monorepo/shared/src/providers/ResourcesProvider/types';
import { SupportResourceType } from 'types';

const altT = (key) => translate('alt', key);
const tourT = (key) => translate('tour', key);

export type Props = {
  activeStep?: any;
  children?: JSX.Element;
  onNextClick?: any;
  onExit?: any;
  activeStepIndex?: number;
  isLastStep?: boolean;
};
const OnboardingModal: React.FC<Props> = ({
  activeStep,
  onNextClick,
  onExit,
  activeStepIndex,
  isLastStep,
}) => {
  const [, { openModal }] = useModal();
  const { resources, error, isLoading } = useSupportResourcesStore();
  const trainingResource = resources.filter((resource) =>
    resource.tags?.some((tag) => tag.value === SupportResourceType.TRAINING_VIDEOS)
  );

  const onGettingStartedVideoBtnClick = useCallback(
    async (resource: Resource) => {
      openModal({
        Title: () => (
          <BaseModalTitle
            text={resource.title}
            className={classes.fullScreenModalTitle}
            onClose={onExit}
          />
        ),
        body: (
          <ResourcePlayer
            selectedResource={{
              data: resource.data,
              url: '',
              title: resource.title,
              mediaType: resource.asset?.type,
              aspectRatio: (resource.data as any)?.aspectRatio,
              heightAdjust: (resource.data as any)?.heightAdjust,
              type: resource.type,
              src: resource.asset?.src,
              vtt: resource.asset?.vtt as string,
              narration: {
                src: resource.asset?.narrationSrc as string,
                vtt: resource.asset?.narrationVtt as string,
              },
            }}
            loadedData={() => null}
            fullScreen
          />
        ),
        ariaLabel: tA11y('aria.modal', { title: resource.title }),
        options: { closeOnBackdropClick: true },
        getContainerStyles: (defaultStyles) => ({ ...defaultStyles, ...{ maxWidth: '100vw' } }),
        getPaperStyles: (defaultStyles) => ({ ...defaultStyles, ...{ maxWidth: '100vw' } }),
      });
    },
    [openModal]
  );

  const onPreviousBtnClick = () => {
    const isPrevious = true;
    onNextClick(isPrevious);
  };

  const onNextBtnClick = () => {
    const isPrevious = false;
    onNextClick(isPrevious);
  };

  const classes = useStyles();

  const isPreviousStepEnabled = !activeStepIndex || activeStepIndex <= 0;
  return (
    <Box className={classes.modal}>
      {activeStep && activeStep.text && (
        <Box className={classes.container}>
          <Box className={classes.imgContainer}>
            <img src={activeStep.media?.src} className={classes.img} alt={altT(activeStep.name)} />
          </Box>
          <Typography variant="body2">
            <span
              id="onboardingContextBox"
              className={classes.main}
              dangerouslySetInnerHTML={{ __html: activeStep.text }}
            />
          </Typography>
          <Box className={classes.buttonBox}>
            <ButtonWithFontAwesome
              onClick={
                isPreviousStepEnabled
                  ? () => onGettingStartedVideoBtnClick(trainingResource[0])
                  : onPreviousBtnClick
              }
              text={
                isPreviousStepEnabled ? tourT('watchGettingStartedVideo') : tourT('previousStep')
              }
              startIcon={isPreviousStepEnabled ? '' : faArrowLeft}
              aria-label={
                isPreviousStepEnabled ? tourT('watchGettingStartedVideo') : tourT('previousStep')
              }
              disabled={isPreviousStepEnabled && (error || isLoading)}
              color="primary"
              variant="outlined"
            />
            <ButtonWithFontAwesome
              onClick={onExit}
              text="Skip Tutorial"
              aria-label="Skip Tutorial"
              color="primary"
              variant="contained"
              startIcon={faArrowToRight}
            />
            <ButtonWithFontAwesome
              onClick={onNextBtnClick}
              text={isLastStep ? 'Get Started' : 'Next Step'}
              startIcon={faArrowRight}
              aria-label={isLastStep ? 'Get Started' : 'Next Step'}
              color="primary"
              variant="contained"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'center',
    padding: '32px',
    width: '800px',
  },
  modal: {
    display: 'flex',
    flex: 1,
    backgroundColor: 'white',
    justifyContent: 'center',
  },
  main: {
    textAlign: 'left',
    '& p': {
      ...theme.typography.subtitle1,
      lineHeight: '24px',
      margin: '5px 0px 20px',
    },
    '& h3': {
      fontSize: '28px',
      margin: '5px 0px 0px',
      fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    },
  },
  buttonBox: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  imgContainer: {
    height: '475px',
    width: '700px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  img: {
    height: 'auto',
    width: '100%',
  },
  fullScreenModalTitle: {
    alignItems: 'center',
    zIndex: 1,
    height: '60px',
    maxHeight: '60px',
    boxShadow:
      '0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)',
    '& h2': {
      fontWeight: 'bold',
    },
  },
}));

export default OnboardingModal;
